import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, Leaf, Loader2, MessageCircle, Map, BookOpen, Download, Mail, Heart, Compass, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer
} from "recharts";
import "../styles/emergence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  fr: {
    stade_changement: "Stade du changement",
    etat_emotionnel: "État émotionnel",
    energie: "Énergie",
    freins: "Freins",
    vision: "Clarté de vision",
    tolerance_incertitude: "Tolérance incertitude",
    soutien: "Soutien",
    rapport_temps: "Rapport au temps",
    auto_soin: "Auto-soin",
    attentes: "Attentes"
  },
  en: {
    stade_changement: "Change stage",
    etat_emotionnel: "Emotional state",
    energie: "Energy",
    freins: "Barriers",
    vision: "Vision clarity",
    tolerance_incertitude: "Uncertainty tolerance",
    soutien: "Support",
    rapport_temps: "Time relationship",
    auto_soin: "Self-care",
    attentes: "Expectations"
  }
};

export default function EmergenceResults() {
  const { sessionId } = useParams();
  const { i18n } = useTranslation();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const isFrench = i18n.language === 'fr';
  const labels = CATEGORY_LABELS[i18n.language] || CATEGORY_LABELS.fr;

  useEffect(() => {
    fetchResults();
  }, [sessionId]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/emergence/diagnostic/${sessionId}`);
      setResults(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching results:", error);
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!email) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API}/emergence/diagnostic/send-email`, {
        session_id: sessionId,
        recipient_email: email,
        language: i18n.language
      });
      toast.success(isFrench ? "Email envoyé !" : "Email sent!");
      setEmailModalOpen(false);
      setEmail("");
    } catch (error) {
      toast.error(isFrench ? "Erreur d'envoi" : "Send error");
    } finally {
      setSendingEmail(false);
    }
  };

  const getChartData = () => {
    if (!results?.scores?.categories) return [];
    return Object.entries(results.scores.categories).map(([key, value]) => ({
      category: labels[key] || key,
      score: value,
      fullMark: 100
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "#8FB8A8";
    if (score >= 60) return "#3B7A8C";
    if (score >= 40) return "#D4A574";
    return "#E07A5F";
  };

  const getStageInfo = () => {
    if (!results?.profile) return null;
    const stage = results.profile.stage;
    const stages = {
      fr: {
        contemplation: { name: "Contemplation", desc: "Tu sens qu'un changement est nécessaire mais tu explores encore" },
        preparation: { name: "Préparation", desc: "Tu clarifies ta direction et prépares le terrain" },
        action: { name: "Action", desc: "Tu es en pleine transformation, bravo !" },
        consolidation: { name: "Consolidation", desc: "Tu ancres tes nouveaux acquis" }
      },
      en: {
        contemplation: { name: "Contemplation", desc: "You sense change is needed but still exploring" },
        preparation: { name: "Preparation", desc: "You're clarifying your direction and preparing" },
        action: { name: "Action", desc: "You're in full transformation, well done!" },
        consolidation: { name: "Consolidation", desc: "You're anchoring your new gains" }
      }
    };
    return stages[isFrench ? 'fr' : 'en'][stage];
  };

  if (loading) {
    return (
      <div className="emergence-bg min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#3B7A8C] animate-spin mx-auto mb-4" />
          <p className="text-[#7A8B99]">{isFrench ? "Analyse de ton profil..." : "Analyzing your profile..."}</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="emergence-bg min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#7A8B99] mb-4">{isFrench ? "Résultats non trouvés" : "Results not found"}</p>
          <Link to="/emergence/diagnostic">
            <Button className="emergence-btn-primary">{isFrench ? "Refaire le diagnostic" : "Redo diagnostic"}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const chartData = getChartData();
  const stageInfo = getStageInfo();

  return (
    <div className="emergence-bg min-h-screen">
      <header className="emergence-glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/emergence" className="flex items-center gap-2 text-[#3B7A8C] hover:opacity-80" data-testid="back-link">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{isFrench ? "Accueil" : "Home"}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-[#3B7A8C]" />
            <span className="font-heading text-lg text-[#2D5A66]">{isFrench ? "Ton profil" : "Your profile"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEmailModalOpen(true)}
              className="rounded-full border-[#3B7A8C]/20 text-[#3B7A8C]"
              data-testid="send-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {isFrench ? "Recevoir par email" : "Get by email"}
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Stage du changement */}
          {stageInfo && (
            <div className="mb-8 emergence-animate-in">
              <Card className="emergence-card border-0 bg-gradient-to-br from-[#3B7A8C] to-[#2D5A66] text-white">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Compass className="w-8 h-8 text-[#D4A574]" />
                    <div>
                      <p className="text-white/60 text-sm">{isFrench ? "Tu es en phase de" : "You're in the phase of"}</p>
                      <h2 className="font-heading text-2xl font-bold">{stageInfo.name}</h2>
                    </div>
                  </div>
                  <p className="text-white/80 text-lg">{stageInfo.desc}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Score global et radar */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            <Card className="emergence-card border-0">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-[#2D5A66] flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#D4A574]" />
                  {isFrench ? "Ton score global" : "Your overall score"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(59,122,140,0.1)" strokeWidth="12" />
                      <circle cx="64" cy="64" r="56" fill="none" stroke={getScoreColor(results.scores?.overall || 0)} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${((results.scores?.overall || 0) / 100) * 352} 352`} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold text-[#2D5A66]" data-testid="overall-score">{Math.round(results.scores?.overall || 0)}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[#7A8B99] text-sm mb-2">
                      {isFrench ? "Score de préparation au changement" : "Change readiness score"}
                    </p>
                    <p className="text-[#3D4F5F]">
                      {results.scores?.overall >= 70 
                        ? (isFrench ? "Tu as de solides ressources !" : "You have solid resources!")
                        : results.scores?.overall >= 50
                        ? (isFrench ? "Des axes de progression identifiés" : "Areas for growth identified")
                        : (isFrench ? "Un accompagnement sera précieux" : "Support will be valuable")}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="emergence-card border-0">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-[#2D5A66]">
                  {isFrench ? "Vue d'ensemble" : "Overview"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={chartData}>
                      <PolarGrid stroke="rgba(59,122,140,0.2)" />
                      <PolarAngleAxis dataKey="category" tick={{ fill: '#7A8B99', fontSize: 10 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#7A8B99', fontSize: 10 }} />
                      <Radar name="Score" dataKey="score" stroke="#3B7A8C" fill="#3B7A8C" fillOpacity={0.3} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Analyse IA */}
          <Card className="emergence-card border-0 mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-[#2D5A66] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#D4A574]" />
                {isFrench ? "Ton analyse personnalisée" : "Your personalized analysis"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-slate max-w-none text-[#3D4F5F] leading-relaxed" data-testid="ai-analysis">
                {results.ai_analysis?.split('\n').map((p, i) => (
                  <p key={i} className="mb-4 whitespace-pre-wrap">{p}</p>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Prochaines étapes */}
          <Card className="emergence-card border-0 mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-[#2D5A66] flex items-center gap-2">
                <Map className="w-5 h-5 text-[#3B7A8C]" />
                {isFrench ? "Tes prochaines étapes suggérées" : "Your suggested next steps"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results.next_steps?.map((step, idx) => (
                <div key={idx} className="emergence-journal-entry">
                  <div className="flex items-start gap-4">
                    <div className="emergence-step completed">{idx + 1}</div>
                    <div>
                      <h4 className="font-medium text-[#2D5A66] mb-1">{step.title}</h4>
                      <p className="text-[#7A8B99] text-sm">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/emergence/chat" data-testid="go-to-chat">
              <Button className="emergence-btn-primary">
                <MessageCircle className="w-5 h-5 mr-2" />
                {isFrench ? "Parler à mon coach" : "Talk to my coach"}
              </Button>
            </Link>
            <Link to="/emergence/journal" data-testid="go-to-journal">
              <Button variant="outline" className="rounded-full border-2 border-[#3B7A8C] text-[#3B7A8C]">
                <BookOpen className="w-5 h-5 mr-2" />
                {isFrench ? "Ouvrir mon journal" : "Open my journal"}
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2D5A66]">{isFrench ? "Envoyer par email" : "Send by email"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="email"
              placeholder={isFrench ? "Ton email" : "Your email"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 rounded-xl border-2 border-[#3B7A8C]/20"
              data-testid="email-input"
            />
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail || !email}
              className="w-full emergence-btn-primary"
              data-testid="confirm-send-email"
            >
              {sendingEmail ? <Loader2 className="w-5 h-5 animate-spin" /> : (isFrench ? "Envoyer" : "Send")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
